import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  blockEmailBuilderSettingsAutosaveForValidation,
  releaseEmailBuilderSettingsValidationAutosaveBlock,
} from "../emailBuilderSettingsTextAutosaveDeferral";
import { EmailBuilderSettingsTextInput } from "./EmailBuilderSettingsTextInput";
import { FieldStack } from "./controls/FieldStack";
import { EmailBuilderFieldMergeTagPicker } from "./EmailBuilderFieldMergeTagPicker";
import {
  isEmailBuilderLinkUrlFieldValueValid,
  normalizeEmailBuilderLinkUrlFieldValue,
} from "../operations/emailBuilderPublishLinkScan";
import {
  isEmailBuilderSocialLinkUrlFieldValueValid,
  normalizeEmailBuilderSocialLinkUrlFieldValue,
} from "../schema/emailDocumentUrlValidation";

export type TEmailBuilderUrlFieldMode = "link" | "social";

export function UrlField({
  label,
  value,
  onChange,
  mode = "link",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  mode?: TEmailBuilderUrlFieldMode;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const isValidValue =
    mode === "social" ? isEmailBuilderSocialLinkUrlFieldValueValid : isEmailBuilderLinkUrlFieldValueValid;
  const normalizeValue =
    mode === "social" ? normalizeEmailBuilderSocialLinkUrlFieldValue : normalizeEmailBuilderLinkUrlFieldValue;
  const invalidMessage =
    mode === "social"
      ? "Enter a valid web address (e.g. example.com or https://…)."
      : "Enter a valid URL (e.g. example.com or https://…) or merge tag.";

  function validateOnBlur(rawValue: string) {
    const trimmed = rawValue.trim();
    if (trimmed === "") {
      setError(null);
      releaseEmailBuilderSettingsValidationAutosaveBlock();
      return;
    }

    if (!isValidValue(rawValue)) {
      setError(t(invalidMessage));
      blockEmailBuilderSettingsAutosaveForValidation();
      return;
    }

    const normalized = normalizeValue(rawValue);
    if (normalized !== rawValue) {
      onChange(normalized);
    }
    setError(null);
    releaseEmailBuilderSettingsValidationAutosaveBlock();
  }

  function handleValueChange(nextValue: string) {
    onChange(nextValue);
    if (error !== null) {
      setError(null);
    }

    if (nextValue.trim() === "" || isValidValue(nextValue)) {
      releaseEmailBuilderSettingsValidationAutosaveBlock();
    }
  }

  return (
    <FieldStack label={label}>
      <EmailBuilderSettingsTextInput
        label={label}
        labelHidden
        value={value}
        error={error !== null}
        helpText={error ?? undefined}
        onChange={event => handleValueChange(event.target.value)}
        onBlur={() => validateOnBlur(value)}
        trailingButtons={
          mode === "link"
            ? [
                <EmailBuilderFieldMergeTagPicker
                  key="merge-tags"
                  value={value}
                  variant="link"
                  onInsert={nextValue => {
                    handleValueChange(nextValue);
                  }}
                />,
              ]
            : undefined
        }
      />
    </FieldStack>
  );
}
